import {useTranslation} from 'react-i18next';
import {useFormikContext} from 'formik';
import {useRef, useState, useMemo} from 'react';
import {Button, Helper} from '@tickchak/react-share/app';
import {symbolDefDark, add} from '../../../../../../../assets/symbols/symbols/Symbols.css';
import TicketCreditsIcon from '../../../../../../../assets/icons/TicketCreditsIcon';
import {PriceGroup} from '../../../../../../../redux/subscription/subscription.type';
import PricingGroupPop from '../../../../pricingGroupPop/PricingGroupPop';
import useShow from '../../../../../../../hooks/useShow';
import List from '../../../../../../../component/list/List';
import NumberInput from '../../../../../../../component/input/NumberInput';
import * as css from '../../PricingStep/PricingStep.css';
import {SubscriptionFormValues} from '../../../createSubscriptionForm.types';
import PriceGroupButtonWithMenu from './PriceGroupButtonWithMenu';
import {usePriceGroups} from '../../PricingStep/hooks/usePriceGroups';
import {formatDecimal} from '../../../../../../../library/numberFunction';

function PriceGroupsSection() {
    const priceGroupPopShow = useShow();
    const {priceGroups, handleSavePriceGroup} = usePriceGroups(priceGroupPopShow);
    const {t} = useTranslation('subscriptions');
    const {values, setFieldValue} = useFormikContext<SubscriptionFormValues>();
    const priceGroupsListShow = useShow();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [editingPriceGroupId, setEditingPriceGroupId] = useState<number | null>(null);
    const refList = useRef(null);

    const priceGroupIds = values.priceGroupIds || [];
    const selectedPriceGroups = (priceGroups || []).filter((pg: PriceGroup) => priceGroupIds.includes(pg.spgId)
    );

    const handleRemovePriceGroup = (priceGroupId: number) => {
        const updatedIds = priceGroupIds.filter((id: number) => id !== priceGroupId);
        setFieldValue('priceGroupIds', updatedIds);
    };

    const handleEditPriceGroup = (e: React.MouseEvent, priceGroup: PriceGroup) => {
        e.stopPropagation();
        setEditingPriceGroupId(priceGroup.spgId);
        priceGroupPopShow.open();
    };

    const handleTogglePriceGroup = (priceGroupId: number) => {
        if(priceGroupIds.includes(priceGroupId)) {
            const updatedIds = priceGroupIds.filter((id: number) => id !== priceGroupId);
            setFieldValue('priceGroupIds', updatedIds);
        } else {
            const updatedIds = [...priceGroupIds, priceGroupId];
            setFieldValue('priceGroupIds', updatedIds);
        }
    };

    const handleCreateNewPriceGroup = () => {
        setEditingPriceGroupId(null);
        priceGroupsListShow.close();
        priceGroupPopShow.open();
    };

    // Prepare list items for List component
    const priceGroupsListItems = useMemo(() => {
        const newPriceGroupItem = {
            key: 'new',
            text: `+ ${t('newPriceGroup')}`,
            onClick: handleCreateNewPriceGroup
        };

        const priceGroupItems = priceGroups?.map((priceGroup: PriceGroup) => {
            const isSelected = priceGroupIds.includes(priceGroup.spgId);
            return {
                key: priceGroup.spgId,
                text: `${priceGroup.title} (${formatDecimal(priceGroup.minPrice)}-${formatDecimal(priceGroup.maxPrice)}₪)`,
                type: 'checkbox',
                selected: isSelected,
                onClick: (item: any) => {
                    if(item && item.key) {
                        handleTogglePriceGroup(item.key);
                    }
                }
            };
        }).sort((a, b) => {
            if(a.selected && !b.selected) return -1;
            if(!a.selected && b.selected) return 1;
            return 0;
        }) || [];

        return [...priceGroupItems, newPriceGroupItem];
    }, [priceGroups, priceGroupIds, handleTogglePriceGroup, t]);

    const handleTogglePriceGroupsList = (e: React.MouseEvent) => {
        e.stopPropagation();
        if(priceGroupsListShow.isOpen) {
            priceGroupsListShow.close();
        } else {
            priceGroupsListShow.open();
        }
    };

    return (
        <css.sectionWrapper>
            <css.headerTitle $icon={symbolDefDark}>
                {t('redemptionSettings')}
                <Helper
                    title={t('redemptionSettingsHelperTitle')}
                    content={<css.helperContent>{t('redemptionSettingsHelperContent')}</css.helperContent>}
                    size='small'
                />
            </css.headerTitle>
            <css.section>
                {/*<css.questionIcon>?</css.questionIcon>*/}
                <css.inputsRow>
                    <css.inputWrapper>
                        <css.localInputWrapper>
                            <NumberInput
                                name="redemptionPrice"
                                text={t('price')}
                                icon={null}
                                className=""
                                forwardRef={null}
                                events={{
                                    onClick: priceGroupPopShow.open
                                }}
                            />
                        </css.localInputWrapper>
                    </css.inputWrapper>
                    <css.inputWrapper>
                        <css.localInputWrapper>
                            <NumberInput
                                name="redemptionUpToPrice"
                                text={t('upToPrice')}
                                icon={null}
                                className=""
                                forwardRef={null}
                                events={{
                                    onClick: priceGroupPopShow.open
                                }}
                            />
                        </css.localInputWrapper>
                    </css.inputWrapper>
                    <Button variant="brand" size="category" onClick={priceGroupPopShow.open} >{t('add')}</Button>
                </css.inputsRow>
                <css.priceGroups>
                    {selectedPriceGroups.map((priceGroup: PriceGroup) => (
                        <PriceGroupButtonWithMenu
                            key={priceGroup.spgId}
                            priceGroup={priceGroup}
                            onEdit={handleEditPriceGroup}
                            onDelete={() => handleRemovePriceGroup(priceGroup.spgId)}
                            t={t}
                        />
                    ))}
                    <css.addPriceGroup ref={dropdownRef}>
                        <Button iconStart={<img src={add} width={16} height={16} alt="" />} variant="brand" size="category" onClick={handleTogglePriceGroupsList} />
                        <css.addPriceGroupText onClick={handleTogglePriceGroupsList} ref={refList} >{t('changePriceGroup')}</css.addPriceGroupText>
                        {priceGroupsListShow.isOpen && (
                            <List
                                list={priceGroupsListItems}
                                showClass={priceGroupsListShow.showClass}
                                close={priceGroupsListShow.close}
                                top={27}
                                fixedTop={refList.current.getBoundingClientRect().y}
                                width="300px"
                                left={refList.current?.getBoundingClientRect().x-40 + 'px'} 

                            />
                        )}
                    </css.addPriceGroup>
                </css.priceGroups>
                <css.descriptionText>{t('priceGroupDescription')}</css.descriptionText>
                <css.localInputWrapper>
                    <NumberInput
                        name="credits"
                        text={t('ticketCredits')}
                        icon={<TicketCreditsIcon />}
                        className=""
                        forwardRef={null}
                    />
                    <Helper
                        title={t('ticketCreditsHelperTitle')}
                        content={t('ticketCreditsHelperContent')}
                        size='small'
                    />
                </css.localInputWrapper>
            </css.section>

            {priceGroupPopShow.isOpen && (
                <PricingGroupPop
                    show={priceGroupPopShow}
                    onSave={async (values) => {
                        await handleSavePriceGroup(values, editingPriceGroupId);
                        setEditingPriceGroupId(null);
                    }}
                    priceGroupId={editingPriceGroupId}
                    setPriceGroupId={setEditingPriceGroupId}
                    priceGroups={priceGroups}
                />
            )}
        </css.sectionWrapper>
    );
}

export default PriceGroupsSection;

